import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

export default class extends Controller {
  static targets = ["sidebar", "backdrop"]

  connect() {
    this.applyTheme()
    this.applyStoredWorkspace()
    this.themeTimer = window.setInterval(() => this.applyTheme(), 60_000)
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") this.applyTheme()
    }
    document.addEventListener("visibilitychange", this.visibilityHandler)
  }

  disconnect() {
    window.clearInterval(this.themeTimer)
    document.removeEventListener("visibilitychange", this.visibilityHandler)
  }

  applyTheme() {
    const automaticTheme = this.automaticTheme()

    try {
      const override = sessionStorage.getItem("echoage-admin-theme-override")
      const overrideUntil = Number(sessionStorage.getItem("echoage-admin-theme-override-until"))

      if ((override === "dark" || override === "light") && overrideUntil > Date.now()) {
        document.documentElement.dataset.adminTheme = override
        return
      }

      sessionStorage.removeItem("echoage-admin-theme-override")
      sessionStorage.removeItem("echoage-admin-theme-override-until")
      localStorage.removeItem("echoage-admin-theme")
    } catch (_error) {
      // The automatic theme does not depend on browser storage.
    }

    document.documentElement.dataset.adminTheme = automaticTheme
  }

  toggleTheme() {
    const next = document.documentElement.dataset.adminTheme === "dark" ? "light" : "dark"
    document.documentElement.dataset.adminTheme = next

    try {
      sessionStorage.setItem("echoage-admin-theme-override", next)
      sessionStorage.setItem("echoage-admin-theme-override-until", this.nextThemeBoundary().getTime().toString())
    } catch (_error) {
      // The button still changes the current page when storage is unavailable.
    }
  }

  automaticTheme(now = new Date()) {
    return now.getHours() >= 19 || now.getHours() < 7 ? "dark" : "light"
  }

  nextThemeBoundary(now = new Date()) {
    const boundary = new Date(now)

    if (now.getHours() < 7) {
      boundary.setHours(7, 0, 0, 0)
    } else if (now.getHours() < 19) {
      boundary.setHours(19, 0, 0, 0)
    } else {
      boundary.setDate(boundary.getDate() + 1)
      boundary.setHours(7, 0, 0, 0)
    }

    return boundary
  }

  applyStoredWorkspace() {
    const forcedWorkspace = this.element.dataset.adminWorkspacePage || document.documentElement.dataset.adminWorkspaceForced
    if (forcedWorkspace === "marketing" || forcedWorkspace === "regular") {
      document.documentElement.dataset.adminWorkspace = forcedWorkspace
      localStorage.setItem("echoage-admin-workspace", forcedWorkspace)
      return
    }

    const workspace = localStorage.getItem("echoage-admin-workspace") === "marketing" ? "marketing" : "regular"
    document.documentElement.dataset.adminWorkspace = workspace
  }

  chooseWorkspace(event) {
    const workspace = event.currentTarget.dataset.workspace === "marketing" ? "marketing" : "regular"
    document.documentElement.dataset.adminWorkspace = workspace

    try {
      localStorage.setItem("echoage-admin-workspace", workspace)
    } catch (_error) {
      // The link still performs its normal Turbo navigation without storage.
    }
  }

  openMenu() {
    document.body.classList.add("admin-menu-open")
  }

  closeMenu() {
    document.body.classList.remove("admin-menu-open")
  }

  openModal(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.modalTarget
    document.getElementById(id)?.classList.add("is-open")
  }

  closeModal(event) {
    event?.preventDefault()
    event?.currentTarget?.closest(".admin-modal")?.classList.remove("is-open")
  }

  closeModalOnBackdrop(event) {
    if (event.target === event.currentTarget) event.currentTarget.classList.remove("is-open")
  }

  async loadDrawer(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.drawerUrl || event.currentTarget.href
    const root = document.getElementById("admin-drawer-root")
    if (!url || !root) return

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!response.ok) throw new Error("The editor could not be loaded.")
      root.innerHTML = await response.text()
      document.body.style.overflow = "hidden"
    } catch (error) {
      Swal.fire({ icon: "error", title: "Couldn’t open editor", text: error.message })
    }
  }

  closeDrawer(event) {
    event?.preventDefault()
    const root = document.getElementById("admin-drawer-root")
    if (root) root.innerHTML = ""
    document.body.style.overflow = ""
  }

  copy(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.copyValue
    if (!value) return

    navigator.clipboard.writeText(value).then(() => {
      Swal.fire({ toast: true, position: "bottom-end", icon: "success", title: "Copied to clipboard", timer: 1800, showConfirmButton: false })
    })
  }
}
